import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { SubscriptionPreview } from '@hero/core';
import { cn } from '@hero/web-lib';
import { ArrowRightIcon, CalendarIcon } from '@hero/web-icons';
import { Price } from '../../../app/Price';
import { Button } from '../../../ui/Button/Button';
import { BillingTrustStrip } from '../BillingTrustStrip/BillingTrustStrip';

export type OrderSummaryVariant = 'pay' | 'free' | 'custom';

export interface BillingOrderSummaryProps {
  /**
   * Canonical preview from `computeSubscriptionPreview()`. `null` when no
   * plan is selected yet (or the variant is `custom`).
   */
  preview: SubscriptionPreview | null;
  /** Human-readable plan name shown next to line items. */
  planTitle?: string;
  /** Already-formatted "Renews on …" date label. */
  nextRenewalLabel?: string;
  variant: OrderSummaryVariant;
  onPrimaryClick: () => void;
  isSubmitting?: boolean;
  className?: string;
}

/**
 * Sticky right-column order summary used by the redesigned billing
 * checkout. Owns the totals layout, primary CTA, and trust strip.
 * Pricing math is supplied as a canonical `SubscriptionPreview`; the
 * component stays presentational.
 */
export function BillingOrderSummary({
  preview,
  planTitle,
  nextRenewalLabel,
  variant,
  onPrimaryClick,
  isSubmitting,
  className,
}: BillingOrderSummaryProps) {
  const { t } = useTranslation('m-billing');
  return (
    <aside
      className={cn(
        'sticky top-6 flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm',
        className,
      )}>
      <header className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-gray-900">
          {t('checkout.order_summary.title')}
        </h3>
        <p className="text-xs text-gray-500">{t('checkout.order_summary.description')}</p>
      </header>

      <SummaryBody
        preview={preview}
        planTitle={planTitle}
        nextRenewalLabel={nextRenewalLabel}
        variant={variant}
        t={t}
      />

      <Button
        size="lg"
        onClick={onPrimaryClick}
        disabled={isSubmitting || (!preview && variant !== 'custom')}
        className="w-full">
        <span className="inline-flex items-center gap-2">
          {primaryLabel(variant, isSubmitting, t)}
          {variant === 'pay' && !isSubmitting && (
            // RTL: arrow points "forward" in the language direction.
            // Flipping via `rtl:rotate-180` keeps a single icon asset
            // and matches the codebase-wide pattern (see
            // `RecentBillingsList`, `ApplicationsSummaryCard`, etc.).
            <ArrowRightIcon className="size-4 rtl:rotate-180" />
          )}
        </span>
      </Button>

      <div className="border-t border-gray-100 pt-4">
        <BillingTrustStrip />
      </div>
    </aside>
  );
}

interface SummaryBodyProps {
  preview: SubscriptionPreview | null;
  planTitle?: string;
  nextRenewalLabel?: string;
  variant: OrderSummaryVariant;
  t: TFunction;
}

function SummaryBody({
  preview,
  planTitle,
  nextRenewalLabel,
  variant,
  t,
}: SummaryBodyProps) {
  if (variant === 'custom') {
    return (
      <EmptyBlock
        title={t('checkout.order_summary.custom.title')}
        body={t('checkout.order_summary.custom.body')}
      />
    );
  }
  if (variant === 'free' && preview) {
    return (
      <div className="flex flex-col gap-3 rounded-xl bg-emerald-50/60 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-900">{planTitle ?? preview.planCode}</span>
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            {t('checkout.order_summary.free.badge')}
          </span>
        </div>
        <p className="text-xs text-emerald-700">
          {t('checkout.order_summary.free.body')}
        </p>
      </div>
    );
  }
  if (!preview) {
    return (
      <EmptyBlock
        title={t('checkout.order_summary.empty.title')}
        body={t('checkout.order_summary.empty.body')}
      />
    );
  }

  const intervalLabel = t(`subscription.interval_label.${preview.interval}`);

  return (
    <div className="flex flex-col gap-4">
      {/*
        Plan summary block — anchors the big subtotal in the user's
        selections without the "× per-truck × trucks × months"
        multiplication chain (per-truck/month already headlines every
        plan card, so repeating it here was redundant). Reads as:

          {planTitle · interval}
          {subtotal}                       ← the actual pre-tax number
          {N trucks committed}
          {M months}
          auto-renews on …

        Tax is rendered as a separate line below; the standalone
        "Subtotal" row was removed since this block now is the subtotal.
      */}
      <div className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {t('checkout.order_summary.plan_heading', {
            planTitle: planTitle ?? preview.planCode,
            interval: intervalLabel.toLocaleLowerCase(),
          })}
        </span>

        <Price
          amount={preview.subtotal}
          currency={preview.currency}
          size="xl"
          className="font-bold text-gray-900"
        />

        <ul className="flex flex-col gap-0.5 text-xs text-gray-600">
          <li>
            {preview.billingBasis === 'per_order'
              ? // Total orders for the period, not the monthly commitment
                // — this is the number the customer is actually buying and
                // the one overage is measured against.
                t('checkout.order_summary.orders_factor', {
                  count: preview.committedQtyForPeriod,
                })
              : t('checkout.order_summary.trucks_factor', {
                  count: preview.committedQty,
                })}
          </li>
          <li>
            {t('checkout.order_summary.months_factor', {
              count: preview.monthsInInterval,
            })}
          </li>
        </ul>

        {nextRenewalLabel && (
          <div className="mt-1 flex items-center gap-1.5 border-t border-gray-200/70 pt-2.5 text-xs text-gray-600">
            <CalendarIcon className="size-3.5 shrink-0 text-gray-400" />
            <span>
              {t('checkout.order_summary.renews_on', { date: nextRenewalLabel })}
            </span>
          </div>
        )}
      </div>

      {preview.tax && (
        <SummaryLine
          label={t('checkout.order_summary.line_vat', {
            percent: Math.round(preview.tax.percentage * 100),
          })}
          amount={preview.tax.amount}
          currency={preview.currency}
          muted
        />
      )}

      <div className="mt-1 flex items-baseline justify-between gap-3 rounded-xl bg-gray-50 px-4 py-3">
        <span className="text-sm font-semibold text-gray-900">
          {t('checkout.order_summary.line_total_due_today')}
        </span>
        <Price
          amount={preview.total}
          currency={preview.currency}
          size="xl"
          className="font-bold text-gray-900"
        />
      </div>
    </div>
  );
}

interface SummaryLineProps {
  label: string;
  amount: number;
  currency: SubscriptionPreview['currency'];
  sublabel?: React.ReactNode;
  muted?: boolean;
}

function SummaryLine({ label, amount, currency, sublabel, muted }: SummaryLineProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex flex-col">
        <span
          className={cn(
            'text-sm',
            muted ? 'text-gray-500' : 'font-medium text-gray-900',
          )}>
          {label}
        </span>
        {sublabel && (
          <span className="text-[11px] text-gray-400">{sublabel}</span>
        )}
      </div>
      <Price
        amount={amount}
        currency={currency}
        className={cn(
          'text-sm tabular-nums',
          muted ? 'text-gray-500' : 'font-semibold text-gray-900',
        )}
      />
    </div>
  );
}

function EmptyBlock({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-8 text-center">
      <span className="text-sm font-medium text-gray-700">{title}</span>
      <span className="text-xs text-gray-500">{body}</span>
    </div>
  );
}

function primaryLabel(
  variant: OrderSummaryVariant,
  isSubmitting: boolean | undefined,
  t: TFunction,
): string {
  if (isSubmitting) return t('checkout.order_summary.cta.submitting');
  // `pay` → "Pay Subscription"; `free` → "Switch to Free"; `custom` →
  // "Contact Sales". The single key per variant keeps copy edits a
  // pure-i18n change (no component wiring needed).
  return t(`checkout.order_summary.cta.${variant}`);
}
